/**
 * Génère une version courte du texte publicitaire pour l'affichage en vitrine
 * en conservant les informations essentielles et en réduisant la longueur.
 */
export const generateShowcaseText = (advertisingText: string): string => {
  if (!advertisingText) return '';

  // Nettoyer le texte d'entrée
  const cleanText = advertisingText
    .replace(/\s+/g, ' ')
    .trim();

  // Diviser en phrases
  const sentences = cleanText.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0);
  
  if (sentences.length === 0) return '';

  // Mots-clés importants pour l'immobilier
  const importantKeywords = [
    // Types de biens
    'appartement', 'maison', 'villa', 'duplex', 'studio', 'loft', 'penthouse',
    // Caractéristiques attractives
    'vue mer', 'vue dégagée', 'terrasse', 'balcon', 'jardin', 'piscine', 'parking', 'garage',
    'ascenseur', 'climatisation', 'cheminée', 'cave', 'cellier',
    // Localisation
    'centre-ville', 'proche', 'quartier', 'résidence', 'standing', 'calme',
    // État et qualité
    'rénové', 'neuf', 'moderne', 'lumineux', 'spacieux', 'exceptionnel', 'rare',
    // Surface et pièces
    'm²', 'pièces', 'chambres', 'salon', 'cuisine'
  ];

  // Fonction pour calculer le score d'importance d'une phrase
  const calculateSentenceScore = (sentence: string): number => {
    const lowerSentence = sentence.toLowerCase();
    let score = 0;

    // Points pour les mots-clés importants
    importantKeywords.forEach(keyword => {
      if (lowerSentence.includes(keyword)) {
        score += 2;
      }
    });

    // Points supplémentaires pour les informations de surface (m²)
    if (lowerSentence.match(/\d+\s*m²/)) {
      score += 3;
    }

    // Points pour les nombres (souvent des caractéristiques importantes)
    const numberMatches = lowerSentence.match(/\d+/g);
    if (numberMatches) {
      score += numberMatches.length;
    }

    // Pénalité pour les phrases trop courtes (moins de 20 caractères)
    if (sentence.trim().length < 20) {
      score -= 2;
    }

    // Bonus pour la première phrase (souvent l'accroche)
    if (sentences.indexOf(sentence) === 0) {
      score += 1;
    }

    return score;
  };

  // Calculer les scores pour toutes les phrases
  const sentencesWithScores = sentences.map(sentence => ({
    text: sentence.trim(),
    score: calculateSentenceScore(sentence),
    length: sentence.trim().length
  }));

  // Trier par score décroissant
  sentencesWithScores.sort((a, b) => b.score - a.score);

  // Sélectionner les meilleures phrases en respectant une longueur cible
  const targetLength = 200; // Longueur cible pour le texte vitrine
  let selectedSentences: string[] = [];
  let currentLength = 0;

  for (const sentenceData of sentencesWithScores) {
    const potentialLength = currentLength + sentenceData.length + (selectedSentences.length > 0 ? 2 : 0); // +2 pour ". "
    
    // Ajouter la phrase si elle ne dépasse pas trop la longueur cible
    if (potentialLength <= targetLength + 50) { // Tolérance de 50 caractères
      selectedSentences.push(sentenceData.text);
      currentLength = potentialLength;
    }
    
    // Arrêter si on a au moins 2 phrases et qu'on approche de la longueur cible
    if (selectedSentences.length >= 2 && currentLength >= targetLength * 0.8) {
      break;
    }
  }

  // Si aucune phrase n'a été sélectionnée ou si le résultat est trop court, 
  // prendre au minimum la première phrase
  if (selectedSentences.length === 0 || currentLength < 50) {
    selectedSentences = [sentences[0].trim()];
  }

  // Reconstituer le texte en respectant l'ordre original des phrases
  const finalSentences: string[] = [];
  sentences.forEach(originalSentence => {
    const trimmedOriginal = originalSentence.trim();
    if (selectedSentences.includes(trimmedOriginal)) {
      finalSentences.push(trimmedOriginal);
    }
  });

  // Joindre les phrases sélectionnées
  let showcaseText = finalSentences.join('. ');

  // Nettoyer et finaliser
  showcaseText = showcaseText
    .replace(/\s+/g, ' ')
    .trim();

  // Ajouter un point final si nécessaire
  if (!showcaseText.endsWith('.') && !showcaseText.endsWith('!') && !showcaseText.endsWith('?')) {
    showcaseText += '.';
  }

  // Si le texte est encore trop long, le tronquer intelligemment
  if (showcaseText.length > 250) {
    // Trouver le dernier point avant la limite
    const lastPeriodIndex = showcaseText.lastIndexOf('.', 250);
    if (lastPeriodIndex > 100) { // S'assurer qu'on garde au moins 100 caractères
      showcaseText = showcaseText.substring(0, lastPeriodIndex + 1);
    } else {
      // Sinon, tronquer au dernier espace avant la limite
      const lastSpaceIndex = showcaseText.lastIndexOf(' ', 247);
      showcaseText = showcaseText.substring(0, lastSpaceIndex) + '...';
    }
  }

  return showcaseText;
};